import React, { useEffect, useState, useCallback } from "react";
import { TableEmpty } from "@finance/components/DesignSystem";
import { api, errMsg } from "@finance/lib/api";
import { toast } from "sonner";
import Layout, { PageHeader } from "@finance/components/Layout";
import { Button } from "@finance/components/ui/button";
import { Switch } from "@finance/components/ui/switch";
import { PaperPlaneTilt, LockKeyOpen } from "@phosphor-icons/react";
import { useAuth } from "@finance/context/AuthContext";
import { CreateUserDialog, EditUserDialog, AccessMatrix, UserAuditLog, useCatalog } from "@finance/components/UserAdmin";

const PERMS = [
  { key: "can_bank_balances", label: "Bank Balances" },
  { key: "can_post_cash", label: "Cash Requests & Posting" },
  { key: "can_push_to_payables", label: "Push to Payables" },
  { key: "can_manage_payment_schedule", label: "Manage Payment Schedule" },
  { key: "can_authorize_payments", label: "Authorize Payments" },
  { key: "is_executive", label: "Executive" },
];

export default function UserAccess() {
  const [users, setUsers] = useState([]);
  const [sending, setSending] = useState(null);
  const [unlocking, setUnlocking] = useState(null);
  const [savingPerm, setSavingPerm] = useState(null);
  const [tab, setTab] = useState("users");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const catalog = useCatalog();
  const { user: me } = useAuth();
  const isOwner = !!me?.is_owner;

  const load = useCallback(() => api.get("/users", { params: { all: true } }).then((r) => setUsers(r.data)), []);
  const toggleActive = async (u) => {
    const act = u.is_active === false ? "reactivate" : "deactivate";
    if (!window.confirm(`${act === "deactivate" ? "Deactivate" : "Reactivate"} ${u.name}?`)) return;
    try { await api.post(`/admin/users/${u.id}/${act}`); toast.success(`${u.name} ${act}d`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, [load]);

  const togglePerm = async (u, key, value) => {
    setSavingPerm(`${u.id}:${key}`);
    setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, [key]: value } : x)));
    try {
      await api.put(`/users/${u.id}/permissions`, { [key]: value });
      toast.success(`${u.name}: ${PERMS.find((p) => p.key === key).label} ${value ? "granted" : "revoked"}`);
    } catch (e) {
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, [key]: !value } : x)));
      toast.error(errMsg(e));
    } finally { setSavingPerm(null); }
  };

  const sendReset = async (u) => {
    if (!window.confirm(`Email a password-reset link to ${u.name} (${u.email})?`)) return;
    setSending(u.id);
    try {
      const { data } = await api.post(`/auth/admin-reset/${u.id}`);
      toast.success(`Reset link sent to ${data.sent_to}`);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSending(null); }
  };

  const unlock = async (u) => {
    setUnlocking(u.id);
    try {
      await api.post(`/auth/unlock/${u.id}`);
      toast.success(`${u.name}'s account unlocked.`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setUnlocking(null); }
  };

  return (
    <Layout>
      <PageHeader title="User Access" subtitle="Create and manage users, page access, read-only and authorities. Every change is logged."
        actions={isOwner && <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="new-user-btn" onClick={() => setCreating(true)}>New user</Button>} />
      <div className="space-y-4 p-8">
        {isOwner && <div className="flex gap-2">{[["users", "Users"], ["matrix", "Access overview"], ["audit", "Audit log"]].map(([k, l]) => <button key={k} data-testid={`ua-tab-${k}`} onClick={() => setTab(k)} className={`rounded-sm px-3 py-1.5 text-sm ${tab === k ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-600"}`}>{l}</button>)}</div>}
        {tab === "matrix" && <AccessMatrix users={users} catalog={catalog} />}
        {tab === "audit" && <UserAuditLog />}
        {tab === "users" && <div className="overflow-x-auto rounded-sm border border-gray-200 bg-white" data-testid="user-access-table">
          <table className="w-full text-sm">
            <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
              <th className="px-4 py-2 text-left">Name</th>
              <th className="px-4 py-2 text-left">Email</th>
              <th className="px-4 py-2 text-left">Role</th>
              {PERMS.map((p) => <th key={p.key} className="px-3 py-2 text-center">{p.label}</th>)}
              <th className="px-4 py-2 text-left">Status</th>
              <th className="px-4 py-2 text-right">Password</th>
            </tr></thead>
            <tbody>
              {users.length === 0 && <TableEmpty colSpan={5 + PERMS.length} title="No users yet" message="Invite a team member to give them access." />}
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-2 font-medium">{u.name}{u.is_executive && <span className="ml-2 rounded-sm bg-[#1B4383]/10 px-1.5 py-0.5 text-[10px] font-bold text-[#1B4383]">EXEC</span>}{u.read_only && <span className="ml-2 rounded-sm bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">READ-ONLY</span>}<div className="text-[11px] font-normal text-gray-400">{u.department}</div></td>
                  <td className="px-4 py-2 text-gray-600">{u.email}</td>
                  <td className="px-4 py-2 text-gray-500">{u.role || u.role_code}</td>
                  {PERMS.map((p) => {
                    const locked = p.key === "is_executive" && u.is_owner; // owner exec is immutable
                    return (
                      <td key={p.key} className="px-3 py-2 text-center">
                        <Switch
                          checked={!!u[p.key]}
                          disabled={locked || savingPerm === `${u.id}:${p.key}`}
                          data-testid={`perm-${p.key}-${u.id}`}
                          onCheckedChange={(v) => togglePerm(u, p.key, v)}
                        />
                      </td>
                    );
                  })}
                  <td className="px-4 py-2">
                    {u.is_active === false
                      ? <span data-testid={`user-inactive-${u.id}`} className="inline-flex items-center rounded-sm bg-gray-100 px-2 py-0.5 text-[11px] font-semibold text-gray-500">Deactivated</span>
                      : u.locked
                      ? <span data-testid={`user-locked-${u.id}`} className="inline-flex items-center rounded-sm bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-600">Locked</span>
                      : <span className="text-[11px] text-emerald-600">Active</span>}
                  </td>
                  <td className="px-4 py-2 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {u.locked && (
                        <Button size="sm" variant="outline" disabled={unlocking === u.id} data-testid={`unlock-${u.id}`}
                          className="border-red-200 text-red-600 hover:bg-red-50" onClick={() => unlock(u)}>
                          <LockKeyOpen size={14} className="mr-1" />{unlocking === u.id ? "Unlocking…" : "Unlock"}
                        </Button>
                      )}
                      {isOwner && <Button size="sm" variant="outline" data-testid={`edit-user-${u.id}`} onClick={() => setEditing(u)}>Edit</Button>}
                      {isOwner && !u.is_owner && <Button size="sm" variant="outline" data-testid={`toggle-active-${u.id}`} className={u.is_active === false ? "text-emerald-700" : "text-red-600"} onClick={() => toggleActive(u)}>{u.is_active === false ? "Reactivate" : "Deactivate"}</Button>}
                      <Button size="sm" variant="outline" disabled={sending === u.id} data-testid={`send-reset-${u.id}`} onClick={() => sendReset(u)}>
                        <PaperPlaneTilt size={14} className="mr-1" />{sending === u.id ? "Sending…" : "Send reset link"}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>}
      </div>
      <CreateUserDialog open={creating} onClose={() => setCreating(false)} onDone={load} catalog={catalog} />
      <EditUserDialog user={editing} onClose={() => setEditing(null)} onDone={load} catalog={catalog} />
    </Layout>
  );
}
